import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
    GlobalAccount,
    GUID,
    NoteView,
    OpportunityStatus,
    OpportunityView,
    TaskStatus,
    TaskType,
    TaskView,
} from '@ndvr/customer-api';

import { titleCase } from '@/utils';

import { HouseholdNoteFormType } from '../household/HouseholdNotes/HouseholdNoteForm';
import { OpportunityLink } from '../opportunities/useSubmitOpportunityForm';
import { OpportunityFormType } from '../opportunities/widgets/OpportunityForm';
import { TaskFormType } from '../tasks/widgets/TaskForm';
import {
    CreateNoteDockItem,
    CreateOpportunityDockItem,
    CreateTaskDockItem,
    DockItem,
    DockItemType,
    DockState,
    UpdateNoteDockItem,
    UpdateOpportunityDockItem,
    UpdateTaskDockItem,
} from './dock-state-types';

const DEFAULT_STATE: DockState = {
    activeId: null,
    items: [],
    isMaximized: false,
};

export const DOCK_STATE_KEY = 'dock-state';

const useDockStateQuery = () => {
    return useQuery<DockState>({
        queryKey: [DOCK_STATE_KEY],
        queryFn: () => {
            try {
                const state = localStorage.getItem(DOCK_STATE_KEY);
                if (!state) return DEFAULT_STATE;
                return { ...DEFAULT_STATE, ...JSON.parse(state) };
            } catch (error) {
                return DEFAULT_STATE;
            }
        },
        gcTime: 0,
        staleTime: Infinity,
        refetchOnWindowFocus: 'always',
    });
};
const generateUUID = () => window.crypto.randomUUID();

export const useDockState = () => {
    const queryClient = useQueryClient();
    const dockStateQuery = useDockStateQuery();

    const setDockState = (partialState: Partial<DockState>) => {
        if (localStorage) {
            localStorage.setItem(DOCK_STATE_KEY, JSON.stringify({ ...dockStateQuery.data, ...partialState }));
        }
        queryClient.invalidateQueries({ queryKey: [DOCK_STATE_KEY] });
    };

    const items = dockStateQuery.data?.items ?? [];

    return {
        activeId: dockStateQuery.data?.activeId ?? null,
        items,
        isMaximized: dockStateQuery.data?.isMaximized ?? false,
        setActiveId: (activeId: string | null) => {
            setDockState({
                activeId,
                items:
                    dockStateQuery.data?.items?.map((item) =>
                        item.id === (activeId ?? dockStateQuery.data?.activeId)
                            ? { ...item, openedAt: new Date().toISOString() }
                            : item,
                    ) ?? [],
            });
        },
        toggleMaximized: () => {
            setDockState({ isMaximized: !(dockStateQuery.data?.isMaximized ?? false) });
        },
        removeDockItem: (id: string) => {
            setDockState({
                activeId: dockStateQuery.data?.activeId === id ? null : dockStateQuery.data?.activeId,
                items: dockStateQuery.data?.items?.filter((item) => item.id !== id) ?? [],
            });
        },
        updateDockItem: (dockItem: DockItem) => {
            setDockState({
                items: dockStateQuery.data?.items?.map((item) => (item.id === dockItem.id ? dockItem : item)) ?? [],
            });
        },
        addCreateNoteDockItem: (globalAccountGUID?: GUID<GlobalAccount>, eventGUID?: string) => {
            const id = generateUUID();
            setDockState({
                activeId: id,
                items: [
                    ...(dockStateQuery.data?.items ?? []),
                    {
                        id,
                        type: DockItemType.CREATE_NOTE,
                        openedAt: new Date().toISOString(),
                        data: {
                            globalAccountGUID,
                            subject: '',
                            content: '',
                            showHouseholdInput: !globalAccountGUID,
                            eventGUID,
                        },
                    } satisfies CreateNoteDockItem,
                ],
            });
        },
        addUpdateNoteDockItem: (
            noteGUID: GUID<NoteView>,
            globalAccountGUID: GUID<GlobalAccount> | undefined,
            formValues?: HouseholdNoteFormType,
            viewMode: 'edit' | 'view' = 'edit',
        ) => {
            const noteItem = items.find(
                (item) => item.type === DockItemType.UPDATE_NOTE && item.data.noteGUID === noteGUID,
            );

            if (noteItem) {
                setDockState({
                    activeId: noteItem.id,
                });
                return;
            }

            const id = generateUUID();
            setDockState({
                activeId: id,
                items: [
                    ...(dockStateQuery.data?.items ?? []),
                    {
                        id,
                        type: DockItemType.UPDATE_NOTE,
                        openedAt: new Date().toISOString(),
                        data: {
                            globalAccountGUID,
                            noteGUID,
                            subject: formValues?.subject || '',
                            content: formValues?.content || '',
                            viewMode,
                        },
                    } satisfies UpdateNoteDockItem,
                ],
            });
        },
        addCreateTaskDockItem: (
            globalAccountGUID?: GUID<GlobalAccount>,
            formValues?: TaskFormType,
            eventGUID?: string,
            actionItemGUID?: string,
            noteGUID?: GUID<NoteView>,
        ) => {
            const id = generateUUID();
            setDockState({
                activeId: id,
                items: [
                    ...(dockStateQuery.data?.items ?? []),
                    {
                        id,
                        type: DockItemType.CREATE_TASK,
                        openedAt: new Date().toISOString(),
                        data: {
                            title: formValues?.title || '',
                            description: formValues?.description || '',
                            householdOption: formValues?.householdOption
                                ? formValues?.householdOption
                                : globalAccountGUID
                                  ? { value: globalAccountGUID, label: '' }
                                  : undefined,
                            assigneeOption: formValues?.assigneeOption || { value: '', label: '' },
                            taskTypeOption: formValues?.taskTypeOption || {
                                value: TaskType.GENERAL,
                                label: titleCase(TaskType.GENERAL),
                            },
                            taskStatusOption: formValues?.taskStatusOption || {
                                value: TaskStatus.NEW,
                                label: titleCase(TaskStatus.NEW),
                            },
                            dueDate: formValues?.dueDate || '',
                            globalAccountGUID,
                            showHouseholdInput: !globalAccountGUID,
                            isRecurring: formValues?.isRecurring || false,
                            frequencyOption: formValues?.frequencyOption,
                            eventGUID,
                            actionItemGUID,
                            noteGUID,
                        },
                    } satisfies CreateTaskDockItem,
                ],
            });
        },
        addUpdateTaskDockItem: (
            taskGUID: GUID<TaskView>,
            globalAccountGUID: GUID<GlobalAccount> | undefined,
            formValues: TaskFormType,
            commentDraft: string = '',
            viewMode: 'edit' | 'view' = 'view',
        ) => {
            const taskItem = items.find(
                (item): item is UpdateTaskDockItem =>
                    item.type === DockItemType.UPDATE_TASK && item.data.taskGUID === taskGUID,
            );

            if (taskItem) {
                setDockState({
                    activeId: taskItem.id,
                    items:
                        dockStateQuery.data?.items?.map((item) =>
                            item.id === taskItem.id
                                ? ({
                                      ...taskItem,
                                      openedAt: new Date().toISOString(),
                                      data: {
                                          ...taskItem.data,
                                          ...formValues,
                                          globalAccountGUID,
                                          commentDraft: commentDraft || taskItem.data.commentDraft,
                                          viewMode,
                                      },
                                  } satisfies UpdateTaskDockItem)
                                : item,
                        ) ?? [],
                });
                return;
            }

            const id = generateUUID();
            setDockState({
                activeId: id,
                items: [
                    ...(dockStateQuery.data?.items ?? []),
                    {
                        id,
                        type: DockItemType.UPDATE_TASK,
                        openedAt: new Date().toISOString(),
                        data: {
                            taskGUID,
                            title: formValues.title,
                            description: formValues.description,
                            householdOption: formValues.householdOption,
                            assigneeOption: formValues.assigneeOption,
                            taskTypeOption: formValues.taskTypeOption,
                            taskStatusOption: formValues.taskStatusOption,
                            dueDate: formValues.dueDate,
                            globalAccountGUID,
                            isRecurring: formValues.isRecurring,
                            frequencyOption: formValues.frequencyOption,
                            commentDraft,
                            viewMode,
                        },
                    } satisfies UpdateTaskDockItem,
                ],
            });
        },
        addCreateOpportunityDockItem: (
            prospectGUID?: GUID<GlobalAccount>,
            formValues?: Partial<OpportunityFormType>,
            link?: OpportunityLink,
        ) => {
            const id = generateUUID();
            setDockState({
                activeId: id,
                items: [
                    ...(dockStateQuery.data?.items ?? []),
                    {
                        id,
                        type: DockItemType.CREATE_OPPORTUNITY,
                        openedAt: new Date().toISOString(),
                        data: {
                            name: formValues?.name || '',
                            description: formValues?.description || '',
                            value: formValues?.value || '',
                            prospectOption: formValues?.prospectOption
                                ? formValues?.prospectOption
                                : prospectGUID
                                  ? { value: prospectGUID, label: '' }
                                  : undefined,
                            statusOption: formValues?.statusOption || {
                                value: OpportunityStatus.IDENTIFIED,
                                label: titleCase(OpportunityStatus.IDENTIFIED.replace(/_/g, ' ')),
                            },
                            prospectGUID,
                            showProspectInput: !prospectGUID,
                            link,
                        },
                    } satisfies CreateOpportunityDockItem,
                ],
            });
        },
        addUpdateOpportunityDockItem: (
            opportunityGUID: GUID<OpportunityView>,
            prospectGUID: GUID<GlobalAccount>,
            formValues: OpportunityFormType,
        ) => {
            const opportunityItem = items.find(
                (item) =>
                    item.type === DockItemType.UPDATE_OPPORTUNITY && item.data.opportunityGUID === opportunityGUID,
            );

            if (opportunityItem) {
                setDockState({
                    activeId: opportunityItem.id,
                });
                return;
            }

            const id = generateUUID();
            setDockState({
                activeId: id,
                items: [
                    ...(dockStateQuery.data?.items ?? []),
                    {
                        id,
                        type: DockItemType.UPDATE_OPPORTUNITY,
                        openedAt: new Date().toISOString(),
                        data: {
                            opportunityGUID,
                            name: formValues.name,
                            description: formValues.description,
                            value: formValues.value,
                            prospectOption: formValues.prospectOption,
                            statusOption: formValues.statusOption,
                            prospectGUID,
                        },
                    } satisfies UpdateOpportunityDockItem,
                ],
            });
        },
    };
};
